import { ProcessLocationTypeDto } from '@/api/generated';
import { generalErrorTranslation } from '@/assets/locales/generalError/generalError.translation.en.ts';
import {
  CvSearchTranslation,
  cvSearchTranslation,
} from '@/assets/locales/views/processes/details/cvSearch/processDetails.cvSearch.translation.en.ts';
import {
  ProcessCandidatesListTranslation,
  processCandidatesListTranslation,
} from '@/assets/locales/views/processes/details/list/processCandidatesList.translation.en.ts';
import {
  quickTipsTranslation,
  QuickTipsTranslation,
} from '@/assets/locales/views/processes/details/quickTips/quickTips.translation.en.ts';
import { ProcessTab } from '@/views/processes/details/model/ProcessTabs.model.ts';

export type ProcessDetailsTranslation = {
  basicInformation: {
    budget: string;
    description: string;
    location: string;
    locationType: string;
    title: string;
    tolerance: string;
  };
  buttons: {
    edit: string;
    eRecruiter: string;
    goToProcessList: string;
    hideProcessSummary: string;
    meetings: string;
    runProcess: string;
    showProcessSummary: string;
    startAnalysis: string;
  };
  configuration: {
    cancel: string;
    description: string;
    edit: string;
    editIntakeCall: string;
    emptyNote: string;
    error: string;
    intakeCall: string;
    intakeCallError: string;
    save: string;
    title: string;
  };
  cvSearch: CvSearchTranslation;
  errors: {
    message: string;
  };
  extendedAtsIntegrationTooltip: string;
  intakeCall: {
    marker: {
      copied: string;
      copy: string;
      hint: string;
    };
    meetings: {
      inProgress: string;
      open: string;
      scheduled: string;
    };
    proposals: {
      append: string;
      dismiss: string;
      dismissError: string;
      from: string;
      replace: string;
      title: string;
      use: string;
    };
    teams: {
      notetakerMissing: string;
      schedule: string;
    };
  };
  list: ProcessCandidatesListTranslation;
  locationTypes: Record<ProcessLocationTypeDto, string>;
  owner: {
    label: string;
    readOnly: string;
    unassigned: string;
  };
  quickTips: QuickTipsTranslation;
  tabs: Record<ProcessTab, string>;
  title: string;
};

export const processDetailsTranslation: ProcessDetailsTranslation = {
  basicInformation: {
    budget: 'Budget',
    description: 'Budget and workplace details',
    location: 'Location',
    locationType: 'Work model',
    title: 'Basic information',
    tolerance: 'Tolerance',
  },
  buttons: {
    edit: 'Edit',
    eRecruiter: 'eRecruiter',
    goToProcessList: 'Go to process list',
    hideProcessSummary: 'Hide details',
    meetings: 'Meetings',
    runProcess: 'Run',
    showProcessSummary: 'Show details',
    startAnalysis: 'Start analysis',
  },
  configuration: {
    cancel: 'Cancel',
    description: 'Process description',
    edit: 'Description & intake call',
    editIntakeCall: 'Edit intake call',
    emptyNote: 'No intake call note yet',
    error:
      'Could not save changes. Check whether the process is still awaiting configuration and try again.',
    intakeCall: 'Intake call',
    intakeCallError: 'Could not save the intake call note. Try again.',
    save: 'Save',
    title: 'Process description and intake call',
  },
  cvSearch: cvSearchTranslation,
  errors: {
    message: generalErrorTranslation.default,
  },
  extendedAtsIntegrationTooltip: 'Extended ATS integration',
  intakeCall: {
    marker: {
      copied: 'Marker copied',
      copy: 'Copy {{marker}} for the Teams meeting title',
      hint: 'Put {{marker}} in the title of the intake call in Teams and invite the notetaker: its note becomes the intake call of the process right away, or shows up here as a proposal when the process already has one.',
    },
    meetings: {
      inProgress:
        'Intake call “{{subject}}” from {{date}} has taken place: its note is being prepared and will land here.',
      open: 'Open meeting',
      scheduled:
        'Intake call “{{subject}}” is scheduled for {{date}}. Its note will land here after the meeting.',
    },
    proposals: {
      append: 'Append to the note',
      dismiss: 'Dismiss',
      dismissError: 'Could not dismiss the proposal. Try again.',
      from: 'From the intake call “{{subject}}”, {{date}}',
      replace: 'Replace the note',
      title: 'Intake call note proposals',
      use: 'Use',
    },
    teams: {
      notetakerMissing:
        'No notetaker mailbox is configured: add the notetaker to the attendees yourself.',
      schedule: 'Schedule in Teams',
    },
  },
  list: processCandidatesListTranslation,
  locationTypes: {
    [ProcessLocationTypeDto.Hybrid]: 'Hybrid',
    [ProcessLocationTypeDto.Office]: 'Office',
    [ProcessLocationTypeDto.Remote]: 'Remote',
  },
  owner: {
    label: 'Owner',
    readOnly: 'This process is read-only. It is owned by {{owner}}.',
    unassigned: 'No owner',
  },
  quickTips: quickTipsTranslation,
  tabs: {
    [ProcessTab.CANDIDATE_LIST]: 'Candidates',
    [ProcessTab.CV_SEARCH]: 'CV Search',
    [ProcessTab.PROCESS_INFORMATION]: 'Process Details',
  },
  title: 'Process details',
};
